import {useCallback, useEffect, useRef} from 'react'
import {matchPath, useLocation} from 'react-router-dom'
import useAppStore from '../store'
import {amplitudeService} from '../service/amplitude.services'
import {ROUTE_EVENTS} from '../config/amplitude.routes'
import {resolveNetworkRole, resolvePageEvent} from '../utils/amplitude.utils'

/**
 * Mirrors the Zustand `user` into Amplitude:
 * - sets the userId on the Amplitude session whenever a user is connected
 * - calls reset() when the user logs out, clearing both userId and device id
 *
 * Mounted once in AppLoader so any auth flow that updates the store is
 * automatically reflected in Amplitude.
 */
export const useAmplitudeUserSync = (): void => {
  const userId = useAppStore(state => state.user?.id ?? null)

  useEffect(() => {
    if (!amplitudeService.isInitialized()) return

    if (userId) {
      amplitudeService.setUserId(userId)
    } else {
      amplitudeService.reset()
    }
  }, [userId])
}

/**
 * Extracts the `:networkId` route param from the current pathname using the
 * same patterns declared in ROUTE_EVENTS (all network-scoped routes carry a
 * `:networkId` segment since the Lot 3 URL migration).
 */
const resolveCurrentNetworkId = (pathname: string): string | undefined => {
  for (const pattern of Object.keys(ROUTE_EVENTS)) {
    const match = matchPath(pattern, pathname)
    if (!match) continue
    return (match.params as Record<string, string | undefined>).networkId
  }
  return undefined
}

/**
 * Emits an Amplitude `*PageViewed` event whenever the location changes to a
 * pathname mapped in ROUTE_EVENTS.
 *
 * - Skips firing while session re-hydration is in flight (so the first
 *   page view after a reload carries the userId set by useAmplitudeUserSync).
 * - Deduplicates: emits at most once per `path` (not per pattern), so
 *   navigating between two projects on the same page still fires twice.
 * - Logs a dev-only warning for unmapped patterns to flag drift between
 *   ROUTE_EVENTS and the router config.
 * - Attaches `networkId` and the user's `role` on that network when the
 *   page is project-scoped, so each event carries the role under which the
 *   user is acting (a user may hold different roles on different networks).
 */
export const usePageTracking = (isLoadingSession: boolean): void => {
  const location = useLocation()
  const networkRoles = useAppStore(state => state.user?.networkRoles ?? null)
  const lastPath = useRef<string | null>(null)

  useEffect(() => {
    if (!amplitudeService.isInitialized()) return
    if (isLoadingSession) return
    if (lastPath.current === location.pathname) return

    const networkId = resolveCurrentNetworkId(location.pathname)
    const payload = resolvePageEvent(location.pathname, ROUTE_EVENTS, {
      networkId,
      networkRoles,
    })

    if (!payload) {
      if (import.meta.env.DEV) {
        console.warn(
          `[amplitude] No ROUTE_EVENTS entry for pathname "${location.pathname}". Page view not tracked.`,
        )
      }
      return
    }

    amplitudeService.track(payload.eventName, payload.properties)
    lastPath.current = location.pathname
  }, [location.pathname, isLoadingSession, networkRoles])
}

/**
 * Returns a `trackEvent` function that wraps `amplitudeService.track` and
 * auto-injects the current network context (`networkId` + the user's `role`
 * on that network) on every call.
 *
 * Use this hook from components/screens instead of calling
 * `amplitudeService.track` directly so action events (button clicks,
 * mutations) carry the same role context as page views — a user can act
 * under different roles on different networks, so the role belongs on each
 * event, not on the user identity.
 *
 * Caller-provided `eventProperties` win over auto-injected keys, so you can
 * still override `networkId` or `role` if you have a reason to.
 */
export const useTrackEvent = () => {
  const location = useLocation()
  const networkRoles = useAppStore(state => state.user?.networkRoles ?? null)

  return useCallback(
    (eventName: string, eventProperties?: Record<string, unknown>) => {
      const networkId = resolveCurrentNetworkId(location.pathname)
      const role = resolveNetworkRole(networkRoles, networkId)

      const enriched: Record<string, unknown> = {}
      if (networkId) enriched.networkId = networkId
      if (role) enriched.role = role

      amplitudeService.track(eventName, {...enriched, ...eventProperties})
    },
    [location.pathname, networkRoles],
  )
}
